import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import MenuItem from './MenuItem';
import ActiveMarker from './ActiveMarker';
import { getInitials } from './helpers';
import { useLocation, useNavigate } from 'react-router-dom';
import { HR, OptionButton, Overlay, Card, IconCircle, ApplicationPicker } from './Menu.styles';
import { borderRadius } from '#/common/hooks/useClientTheme';
import { useSwitch } from '@nuvolo/nuux-core/hooks/useSwitch';
import getMenuItems from './menuItems';
import { UserRole } from '#/common/types/userTypes';
import { RoleToRoleNameMap } from './AppMenu';
import { color } from '@nuvolo/nuux-theme/utils';
import { useJiraCollector } from '#/common/hooks/useJiraCollector';
import ApplicationPickerCard from './ApplicationPickerCard';
import FAIcon from '#/common/components/FAIcon';
import traneLogoFull from '#/static/trane-logo.png';

const ProfileName = styled.div`
  line-height: 20px;
  font-size: 16px;
  font-weight: 500;
  color: ${color('custom', 'selected')};
`;

const ProfileRole = styled.div`
  line-height: 16px;
  font-size: 12px;
  font-weight: 400;
  color: ${color('custom', 'selected')};
  opacity: 0.6;
`;

const CollapseToggle = styled.button`
  position: absolute;
  right: -24px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${color('ui', 'dark')};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9997;
  padding: 6px;
  box-sizing: border-box;
  color: ${color('contrast', 'light')};

  &:hover {
    background: ${color('secondary', 'light')};
  }
`;

const ProfileChevronButton = styled.button`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: ${borderRadius?.s || '16px'};
  padding: 10px;
  gap: 6px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${color('custom', 'hoverColor')};

  &:hover {
    background: ${color('secondary', 'light')};
  }
`;

const DesktopMenu: React.FC<{
  logo: React.ReactNode;
  userName?: string;
  userRole?: UserRole;
  selectedIndex: number;
  onLogout: () => Promise<void> | void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}> = ({ logo, userName, userRole, selectedIndex, onLogout, isCollapsed, onToggleCollapse }) => {
  const menuItems = getMenuItems({ role: userRole });
  const menuItemsRefs = menuItems.map(() => useRef<HTMLDivElement>(null));
  const location = useLocation();
  const navigate = useNavigate();

  const { showCollector } = useJiraCollector();
  const { value: isProfileOpen, on: onOpenProfile, off: onCloseProfile } = useSwitch(false);
  const {
    value: isApplicationPickerOpen,
    toggle: toggleApplicationPicker,
    off: closeApplicationPicker,
  } = useSwitch(false);

  useEffect(() => {
    onCloseProfile();
    closeApplicationPicker();
  }, [location.pathname]);

  return (
    <>
      <NuvoBox
        marginTop="auto"
        display="flex"
        justifyContent={isCollapsed ? 'center' : 'flex-start'}>
        <ApplicationPicker
          isDesktop
          active={isApplicationPickerOpen}
          onClick={toggleApplicationPicker}
          style={{
            width: 'auto',
            height: 'auto',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            padding: 16,
            boxSizing: 'border-box',
          }}>
          <div
            style={{
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <FAIcon icon="grid-2" color={color('custom', 'iconGray')} />
          </div>
        </ApplicationPicker>

        <ApplicationPickerCard opened={isApplicationPickerOpen} onClose={closeApplicationPicker} />
      </NuvoBox>
      <NuvoBox>
        <HR />
      </NuvoBox>

      <NuvoBox
        style={{ position: 'relative', width: '100%' }}
        display="flex"
        justifyContent={isCollapsed ? 'center' : 'flex-start'}>
        <NuvoBox
          style={{
            width: isCollapsed ? 38 : 114,
            height: 38,
            padding: 16,
            boxSizing: 'content-box',
            flexShrink: 0,
            overflow: 'hidden',
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
          }}>
          <OptionButton
            onClick={() => {
              navigate('/dashboard');
            }}>
            <div className="logo-wrapper" style={{ position: 'relative', width: 114, height: 38 }}>
              {/* Full Trane wordmark — fades out when collapsed */}
              <img
                src={traneLogoFull}
                alt="Trane"
                style={{
                  width: 114,
                  height: 38,
                  display: 'block',
                  objectFit: 'contain',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  opacity: isCollapsed ? 0 : 1,
                  transition: 'opacity 0.2s ease',
                  pointerEvents: isCollapsed ? 'none' : 'auto',
                }}
              />
              {/* Small icon mark — fades in when collapsed */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: 38,
                  height: 38,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isCollapsed ? 1 : 0,
                  transition: 'opacity 0.2s ease',
                  pointerEvents: isCollapsed ? 'auto' : 'none',
                }}>
                {logo}
              </div>
            </div>
          </OptionButton>
        </NuvoBox>
        <CollapseToggle onClick={onToggleCollapse}>
          <FAIcon
            icon={isCollapsed ? 'chevron-right' : 'chevron-left'}
            size="xs"
            color={color('custom', 'iconGray')}
          />
        </CollapseToggle>
      </NuvoBox>

      <NuvoBox
        display="flex"
        alignItems="stretch"
        flexDirection="column"
        justifyContent="flex-start">
        {menuItems
          .filter((menuItem) => menuItem.shouldShow)
          .map((mi, i) => (
            <NuvoBox key={mi.route} flex={1}>
              <div className="menu-item-wrapper" ref={menuItemsRefs[i]}>
                <MenuItem {...mi} isActive={selectedIndex === i} collapsed={isCollapsed} />
              </div>
            </NuvoBox>
          ))}
        <ActiveMarker
          selectedIndex={selectedIndex}
          menuItemsRefs={menuItemsRefs}
          isCollapsed={isCollapsed}
        />
      </NuvoBox>

      {/* Desktop profile section */}
      <NuvoBox
        style={{ position: 'absolute', bottom: 16, left: 0, width: '100%' }}
        display="flex"
        justifyContent={isCollapsed ? 'center' : 'flex-start'}>
        {userName && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              padding: '8px 16px',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}>
            <IconCircle size={36} style={{ flexShrink: 0 }}>
              {getInitials(userName)}
            </IconCircle>
            {/* Name + role: clip away as panel shrinks */}
            <div
              style={{
                overflow: 'hidden',
                flex: 1,
                marginLeft: 12,
                opacity: isCollapsed ? 0 : 1,
                maxWidth: isCollapsed ? 0 : 160,
                transition: 'opacity 0.2s ease, max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                whiteSpace: 'nowrap',
              }}>
              <ProfileName
                style={{
                  fontSize: 14,
                  lineHeight: '18px',
                  color: color('contrast', 'light'),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {userName}
              </ProfileName>
              <ProfileRole
                style={{
                  fontSize: 11,
                  color: color('custom', 'labelGray'),
                  opacity: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {userRole ? RoleToRoleNameMap[userRole] : ''}
              </ProfileRole>
            </div>
            {/* Chevron: fade out when collapsed, keep in place */}
            <ProfileChevronButton
              onClick={onOpenProfile}
              style={{
                flexShrink: 0,
                opacity: isCollapsed ? 0 : 1,
                pointerEvents: isCollapsed ? 'none' : 'auto',
                transition: 'opacity 0.2s ease',
              }}>
              <FAIcon icon="chevron-right" size="lg" color={color('custom', 'iconGray')} />
            </ProfileChevronButton>
          </div>
        )}
      </NuvoBox>
      {isProfileOpen && (
        <>
          <Overlay onClick={onCloseProfile} />
          <Card
            variant="desktop"
            w={200}
            h={90}
            style={{ left: 'calc(100% + 8px)', bottom: '8px' }}>
            <OptionButton
              size="sm"
              onClick={() => {
                showCollector();
                onCloseProfile();
              }}>
              <FAIcon icon="comment" color={color('custom', 'iconGray')} />
              Contact Us
            </OptionButton>
            <OptionButton size="sm" onClick={onLogout}>
              <FAIcon icon="right-from-bracket" color={color('custom', 'iconGray')} />
              Log Out
            </OptionButton>
          </Card>
        </>
      )}
    </>
  );
};

export default React.memo(DesktopMenu);
